import IProduct from 'models/product';

// Whether a field the AI review failed has changed since: the review is about the value it saw.
// 'unsaved': changed in the form only; 'saved': saved with another value.
export type TAiVerdictChange = 'unsaved' | 'saved' | null;

// The value of a field as the review is sent it (filterProductForAIReview): empty as '', the category by name.
export const reviewValue = (product: Partial<IProduct> | null | undefined, field: string): unknown => {
  if (field === 'category') return product?.foodlaCategory?.name ?? '';
  return (product as Record<string, unknown> | null | undefined)?.[field] ?? '';
};

const NUMBER = /^-?\d+(?:[.,]\d+)?$/;

// Leaves out differences the form makes without a real change: spaces, 39 against "39", "0,5" against 0.5,
// the order of a multiple choice, the order of object keys.
const normalize = (value: unknown): unknown => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const text = value.trim().replace(/\s+/g, ' ');
    return NUMBER.test(text) ? Number(text.replace(',', '.')) : text;
  }
  if (Array.isArray(value)) {
    const items = value.map(normalize);
    return items.every(item => typeof item !== 'object')
      ? [...items].sort((a, b) => String(a).localeCompare(String(b)))
      : items;
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.keys(value as object)
        .filter(key => key !== '__typename')
        .sort()
        .map(key => [key, normalize((value as Record<string, unknown>)[key])])
    );
  }
  return value;
};

export const sameReviewValue = (a: unknown, b: unknown) => JSON.stringify(normalize(a)) === JSON.stringify(normalize(b));

// Back to the reviewed value counts as unchanged. A change counts as saved only when the form holds the
// saved value; a review run on unsaved edits and edited again stays an unsaved change.
// A review from before the run log has no reviewed values: only a change in the form against the saved
// product can be told, never whether the saved value changed since the review.
export const aiVerdictChange = (
  field: string,
  reviewedValues: Record<string, unknown> | null | undefined,
  current: Partial<IProduct> | null | undefined,
  saved: Partial<IProduct> | null | undefined
): TAiVerdictChange => {
  if (!reviewedValues) {
    return sameReviewValue(reviewValue(current, field), reviewValue(saved, field)) ? null : 'unsaved';
  }
  if (!Object.prototype.hasOwnProperty.call(reviewedValues, field)) return null;
  const reviewed = reviewedValues[field];
  if (sameReviewValue(reviewValue(current, field), reviewed)) return null;
  return sameReviewValue(reviewValue(current, field), reviewValue(saved, field)) ? 'saved' : 'unsaved';
};

// The reviewed value for a tooltip, short.
export const describeReviewedValue = (value: unknown) => {
  const text =
    value === null || value === undefined || value === ''
      ? '(empty)'
      : Array.isArray(value)
      ? value.map(item => (typeof item === 'object' ? JSON.stringify(item) : String(item))).join(', ')
      : typeof value === 'object'
      ? (value as { name?: string }).name || JSON.stringify(value)
      : String(value);
  return text.length > 200 ? `${text.slice(0, 200)}…` : text;
};
